import React, {useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faBell,
  faListCheck,
  faPlus,
  faRoute,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const codeStyle = {
  background: "#f3f4f6",
  border: "1px solid #e5e7eb",
  padding: "1px 6px",
  borderRadius: "4px",
  fontSize: "0.78rem",
  color: "#363166",
  fontFamily: "monospace",
};

const infoBoxStyle = {
  marginTop: "14px",
  padding: "10px 12px",
  background: "#fff5f2",
  borderLeft: "3px solid #FF683A",
  borderRadius: "0 6px 6px 0",
  fontSize: "0.82rem",
  color: "#374151",
  lineHeight: 1.55,
};

const FleetTripHelpModal = ({onClose}) => {
  const [activeSection, setActiveSection] = useState("overview");

  const sections = [
    {key: "overview", label: "Overview", icon: faRoute},
    {key: "list", label: "Fleet Trip List", icon: faListCheck},
    {key: "create", label: "Create / Edit", icon: faPlus},
    {key: "notif", label: "Notification History", icon: faBell},
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "780px",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Modal header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
            background: "linear-gradient(135deg, #363166 0%, #4a4490 100%)",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
            <FontAwesomeIcon
              icon={faRoute}
              style={{color: "#FF683A", fontSize: "1.2rem"}}
            />
            <div>
              <div style={{color: "#fff", fontWeight: 700, fontSize: "1rem"}}>
                Fleet Trips — Quick Guide
              </div>
              <div style={{color: "rgba(255,255,255,0.65)", fontSize: "0.75rem"}}>
                How to plan, run, and monitor fleet trips
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "6px",
              color: "#fff",
              cursor: "pointer",
              padding: "6px 10px",
              fontSize: "1rem",
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Section tabs */}
        <div
          style={{
            display: "flex",
            gap: "6px",
            padding: "12px 20px 0",
            borderBottom: "1px solid #e5e7eb",
            flexWrap: "wrap",
          }}
        >
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              style={{
                padding: "7px 14px",
                border: "1.5px solid",
                borderColor:
                  activeSection === s.key ? "#FF683A" : "rgba(54,49,102,0.25)",
                borderBottom:
                  activeSection === s.key
                    ? "1.5px solid #fff"
                    : "1.5px solid rgba(54,49,102,0.25)",
                marginBottom: activeSection === s.key ? "-1px" : "0",
                borderRadius: "6px 6px 0 0",
                background:
                  activeSection === s.key ? "#fff" : "rgba(54,49,102,0.04)",
                color: activeSection === s.key ? "#FF683A" : "#363166",
                fontWeight: activeSection === s.key ? 600 : 400,
                fontSize: "0.82rem",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FontAwesomeIcon icon={s.icon} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Modal body */}
        <div style={{overflowY: "auto", padding: "20px", flex: 1}}>
          {/* ── Overview ── */}
          {activeSection === "overview" && (
            <div style={{color: "#374151", fontSize: "0.88rem", lineHeight: 1.6}}>
              <p style={{margin: "0 0 10px"}}>
                A <strong>Fleet Trip</strong> is a scheduled operation that
                groups multiple GPS locks together. Real-time alerts (lock /
                unlock, tamper, low battery) are sent <strong>only while a trip
                is Active</strong> — not while Scheduled or Completed.
              </p>
              <p style={{margin: "0 0 10px"}}>
                Each GPS lock can only be assigned to one Active or Scheduled
                trip at a time. The daily fleet summary email includes only the
                devices that were on an Active Fleet Trip during the day.
              </p>
              <div style={infoBoxStyle}>
                <strong>Typical workflow:</strong>
                <ol style={{margin: "6px 0 0 18px", padding: 0}}>
                  <li>
                    Go to <em>Create / Edit Fleet Trip</em> and define the trip
                    name, type, schedule, and route.
                  </li>
                  <li>
                    Open the new trip from <em>Fleet Trip List</em>, click the{" "}
                    <strong>📦</strong> icon, and assign the GPS locks.
                  </li>
                  <li>
                    Click <strong>Activate</strong> to start monitoring — alerts
                    begin flowing immediately.
                  </li>
                  <li>
                    Track delivery progress and review alert events in{" "}
                    <em>Notification History</em>.
                  </li>
                  <li>
                    Click <strong>Complete</strong> when the operation is over
                    to stop further alerts.
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* ── Fleet Trip List ── */}
          {activeSection === "list" && (
            <div style={{color: "#374151", fontSize: "0.88rem", lineHeight: 1.6}}>
              <p style={{margin: "0 0 10px"}}>
                <strong>How Fleet Trips work:</strong> A Fleet Trip groups
                multiple GPS locks into a single scheduled operation. Real-time
                alerts (lock / unlock, tamper, battery) are sent{" "}
                <strong>only while a trip is Active</strong> — not while
                Scheduled or Completed.
              </p>
              <p style={{margin: "0 0 10px"}}>
                Use the <strong>📦</strong> icon on each row to assign GPS
                locks, then click <strong>Activate</strong> to start
                monitoring. The daily fleet summary email only includes
                devices that were on an Active Fleet Trip during the day.
              </p>
              <div style={infoBoxStyle}>
                <strong>Row actions:</strong>
                <ul style={{margin: "6px 0 0 18px", padding: 0}}>
                  <li>
                    <code style={codeStyle}>✎</code> Edit fleet trip details.
                  </li>
                  <li>
                    <code style={codeStyle}>📦</code> Manage GPS locks for the
                    trip.
                  </li>
                  <li>
                    <code style={codeStyle}>Activate</code> Start monitoring —
                    alerts begin flowing.
                  </li>
                  <li>
                    <code style={codeStyle}>Complete</code> Stop alerts when
                    the operation is done.
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* ── Create / Edit ── */}
          {activeSection === "create" && (
            <div style={{color: "#374151", fontSize: "0.88rem", lineHeight: 1.6}}>
              <p style={{margin: "0 0 10px"}}>
                A <strong>Fleet Trip</strong> is a scheduled operation that
                groups multiple GPS locks together. Real-time alerts (lock /
                unlock, tamper, low battery) are only sent for locks assigned
                here while the trip status is <strong>Active</strong>.
              </p>
              <p style={{margin: "0 0 10px"}}>
                Each GPS lock can only be assigned to one Active or Scheduled
                trip at a time — try to assign it elsewhere and the form will
                reject the second assignment.
              </p>
              <div style={infoBoxStyle}>
                <strong>Tip:</strong> Saving a trip leaves it in{" "}
                <em>Scheduled</em> status. Alerts won't start until you open it
                from <em>Fleet Trip List</em>, assign locks via the{" "}
                <strong>📦</strong> icon, and click <strong>Activate</strong>.
              </div>
            </div>
          )}

          {/* ── Notification History ── */}
          {activeSection === "notif" && (
            <div style={{color: "#374151", fontSize: "0.88rem", lineHeight: 1.6}}>
              <p style={{margin: "0 0 10px"}}>
                <strong>About notifications:</strong> Alerts are generated only
                for GPS locks assigned to an{" "}
                <strong>Active Fleet Trip</strong>. If a device is not on any
                active fleet trip, all alert channels (lock / unlock, tamper,
                battery) are suppressed.
              </p>
              <p style={{margin: "0 0 10px"}}>
                Use the <em>Fleet Trip List</em> tab to check trip status and
                assign devices. The status filter at the top of the page lets
                you focus on a specific delivery state.
              </p>
              <div style={infoBoxStyle}>
                <strong>Not seeing the alerts you expect?</strong> Confirm
                that:
                <ul style={{margin: "6px 0 0 18px", padding: 0}}>
                  <li>The device is assigned to a fleet trip.</li>
                  <li>That trip's status is <strong>Active</strong>.</li>
                  <li>
                    The relevant channel (WhatsApp / Email / Push) is enabled
                    in <em>Settings → Notification Preferences</em>.
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FleetTripHelpModal;
